import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Ticket } from "lucide-react";
import { api } from "@/lib/api";
import { clearCart } from "@/lib/cart";

export default function OrderConfirmation() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling");
  const [data, setData] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data: d } = await api.get(`/marketplace/order-status/${sessionId}`);
        if (d.payment_status === "paid") { setData(d); setState("paid"); clearCart(); return; }
        if (["failed", "expired"].includes(d.payment_status)) { setData(d); setState("failed"); return; }
      } catch (e) {
        console.warn("Order status poll failed, retrying…", e);
      }
      tries.current += 1;
      if (tries.current >= 12) { setState("timeout"); return; }
      setTimeout(poll, 2500);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  if (state === "polling") return (
    <div className="py-40 text-center" data-testid="order-polling">
      <Loader2 className="animate-spin text-rose-500 mx-auto mb-4" size={36} />
      <p className="text-zinc-300 font-medium">Confirming your payment...</p>
    </div>
  );
  if (state === "failed" || state === "error") return (
    <div className="py-40 text-center" data-testid="order-failed">
      <XCircle className="text-rose-500 mx-auto mb-4" size={44} />
      <p className="text-zinc-200 font-bold text-xl mb-2">Payment didn't go through</p>
      <p className="text-zinc-400 mb-6">Your card was not charged. Your cart is saved — you can try again.</p>
      <Link to="/cart" className="bg-rose-500 hover:bg-rose-600 font-bold px-6 py-3 rounded-full">Back to cart</Link>
    </div>
  );
  if (state === "timeout") return (
    <div className="py-40 text-center" data-testid="order-timeout">
      <p className="text-zinc-200 font-bold text-xl mb-2">Still processing...</p>
      <p className="text-zinc-400 mb-6">We're confirming your payment. Check <Link to="/my-orders" className="text-rose-400">My Orders</Link> in a minute or your email for confirmation.</p>
    </div>
  );

  const orders = data?.orders || [];
  const tickets = data?.tickets || [];
  return (
    <div className="px-6 md:px-12 lg:px-20 py-16 min-h-screen max-w-3xl mx-auto" data-testid="order-confirmed">
      <div className="text-center mb-10">
        <CheckCircle2 className="text-emerald-400 mx-auto mb-4" size={52} />
        <h1 className="font-display font-extrabold text-3xl md:text-4xl">Order confirmed!</h1>
        <p className="text-zinc-400 mt-2">A confirmation email is on its way. Thanks for supporting the Gulf Coast!</p>
      </div>
      {orders.map((o) => (
        <div key={o.id} className="bg-[#111] border border-white/10 rounded-2xl p-6 mb-4" data-testid={`conf-order-${o.order_number}`}>
          <div className="flex items-center justify-between mb-3">
            <p className="font-bold">{o.order_number}</p>
            <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300">Paid</span>
          </div>
          <p className="text-sm text-zinc-400 mb-3">Sold by {o.seller_name}</p>
          {o.items.map((it, i) => (
            <div key={i} className="flex justify-between text-sm text-zinc-300 py-1">
              <span>{it.qty} × {it.product_name}{it.variation_name ? ` (${it.variation_name})` : ""}</span>
              <span>${it.line_total.toFixed(2)}</span>
            </div>
          ))}
          <div className="flex justify-between font-bold pt-3 mt-2 border-t border-white/10"><span>Total</span><span>${o.total.toFixed(2)}</span></div>
        </div>
      ))}
      {tickets.length > 0 && (
        <div className="mt-8">
          <h2 className="font-display font-bold text-xl mb-4 flex items-center gap-2"><Ticket className="text-rose-400" size={20} /> Your tickets</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {tickets.map((t) => (
              <div key={t.id} className="bg-[#111] border border-white/10 rounded-2xl p-5 text-center" data-testid={`conf-ticket-${t.code}`}>
                {t.qr_data_url && <img src={t.qr_data_url} alt="QR" className="w-32 h-32 mx-auto rounded-lg bg-white p-1" />}
                <p className="font-bold mt-3 text-sm">{t.product_name}</p>
                <p className="text-xs text-zinc-400">{t.attendee_name}</p>
                <p className="font-mono text-rose-400 text-sm mt-1">{t.code}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="text-center mt-10 flex flex-wrap justify-center gap-3">
        <Link to="/my-orders" className="bg-rose-500 hover:bg-rose-600 font-bold px-6 py-3 rounded-full" data-testid="conf-my-orders">View my orders</Link>
        <Link to="/marketplace" className="bg-white/10 hover:bg-white/15 font-bold px-6 py-3 rounded-full">Keep shopping</Link>
      </div>
    </div>
  );
}
